import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { Styles } from 'jspdf-autotable';
import DejaVuSansBoldTtf from 'dejavu-fonts-ttf/ttf/DejaVuSans-Bold.ttf';
import DejaVuSansTtf from 'dejavu-fonts-ttf/ttf/DejaVuSans.ttf';
import dayjs from 'dayjs';
import {
  formatBookingCurrency,
  formatBookingExchangeRate,
  formatRevenueAmountPln,
  hasForeignRevenue,
} from './formatBookingCurrency';
import type {
  BookingEventReportDto,
  BookingEventRevenueItemDto,
} from '../Types/bookingEventReport.types';

interface JsPdfWithAutoTable extends jsPDF {
  lastAutoTable?: { finalY: number };
}

const PDF_FONT_FAMILY = 'DejaVuSans';
const PDF_MARGIN_LEFT = 10;
const PDF_MARGIN_RIGHT = 10;

let cachedRegularFontBinary: string | null = null;
let cachedBoldFontBinary: string | null = null;

const arrayBufferToBinaryString = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    let s = '';
    for (let j = 0; j < chunk.length; j++) s += String.fromCharCode(chunk[j]);
    binary += s;
  }
  return binary;
};

const ensureFontsRegistered = async (doc: jsPDF): Promise<void> => {
  if (!cachedRegularFontBinary) {
    const res = await fetch(DejaVuSansTtf);
    cachedRegularFontBinary = arrayBufferToBinaryString(await res.arrayBuffer());
  }
  if (!cachedBoldFontBinary) {
    const res = await fetch(DejaVuSansBoldTtf);
    cachedBoldFontBinary = arrayBufferToBinaryString(await res.arrayBuffer());
  }
  doc.addFileToVFS('DejaVuSans.ttf', cachedRegularFontBinary);
  doc.addFont('DejaVuSans.ttf', PDF_FONT_FAMILY, 'normal');
  doc.addFileToVFS('DejaVuSans-Bold.ttf', cachedBoldFontBinary);
  doc.addFont('DejaVuSans-Bold.ttf', PDF_FONT_FAMILY, 'bold');
};

const formatDate = (value: string): string => {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY') : value;
};

const addTextLine = (
  doc: JsPdfWithAutoTable,
  text: string,
  y: number,
  fontSize: number,
  fontStyle: 'normal' | 'bold',
  color?: [number, number, number]
): number => {
  doc.setFontSize(fontSize);
  doc.setFont(PDF_FONT_FAMILY, fontStyle);
  if (color) {
    doc.setTextColor(...color);
  } else {
    doc.setTextColor(0, 0, 0);
  }
  doc.text(text, PDF_MARGIN_LEFT, y);
  return y + fontSize * 0.45 + 2;
};

export const buildRevenueRows = (
  items: BookingEventRevenueItemDto[]
): { showFx: boolean; head: string[]; body: string[][] } => {
  const showFx = hasForeignRevenue(items);
  return {
    showFx,
    head: [
      'Lp',
      'Nr faktury',
      'Nabywca',
      'Adres',
      'NIP',
      'Kwota',
      ...(showFx ? ['Kurs', 'W PLN'] : []),
    ],
    body: items.map((r) => [
      String(r.lp),
      r.invoiceNumber,
      r.buyer,
      r.buyerAddress ?? '—',
      r.buyerNip ?? '—',
      formatBookingCurrency(r.amount, r.currency),
      ...(showFx ? [formatBookingExchangeRate(r.exchangeRate), formatRevenueAmountPln(r)] : []),
    ]),
  };
};

export const buildRevenueColumnStyles = (showFx: boolean): Record<number, Partial<Styles>> =>
  showFx
    ? {
        0: { cellWidth: 10, halign: 'right' },
        1: { cellWidth: 24 },
        4: { cellWidth: 24 },
        5: { cellWidth: 28, halign: 'right' },
        6: { cellWidth: 17, halign: 'right' },
        7: { cellWidth: 28, halign: 'right' },
      }
    : {
        0: { cellWidth: 12, halign: 'right' },
        1: { cellWidth: 28 },
        4: { cellWidth: 28 },
        5: { cellWidth: 35, halign: 'right' },
      };

export interface BookingEventReportHeaderLayout {
  termY: number;
  companyY: number | null;
  costsStartY: number;
}

/**
 * Computes vertical positions of the PDF header lines. The company line is placed
 * directly under "Termin" only when a non-blank company name is present; otherwise
 * the layout is identical to the one without that line.
 */
export const buildHeaderLayout = (
  titleLineCount: number,
  companyName: string | null | undefined
): BookingEventReportHeaderLayout => {
  const termY = 16 + titleLineCount * 7 + 2;
  const companyY = companyName?.trim() ? termY + 6 : null;
  return { termY, companyY, costsStartY: (companyY ?? termY) + 8 };
};

export const createBookingEventReportPdfBlob = async (
  data: BookingEventReportDto
): Promise<Blob> => {
  const doc = new jsPDF({ orientation: 'portrait', format: 'a4' }) as JsPdfWithAutoTable;
  await ensureFontsRegistered(doc);
  doc.setFont(PDF_FONT_FAMILY, 'normal');

  const pageWidth = doc.internal.pageSize.getWidth();

  // Title
  const title = `Rozliczenie imprezy: ${data.offerName ?? 'Bez nazwy'}`;
  doc.setFontSize(14);
  doc.setFont(PDF_FONT_FAMILY, 'bold');
  doc.setTextColor(0, 0, 0);
  const titleLines = doc.splitTextToSize(title, pageWidth - PDF_MARGIN_LEFT - PDF_MARGIN_RIGHT);
  doc.text(titleLines, PDF_MARGIN_LEFT, 16);

  const { termY, companyY, costsStartY } = buildHeaderLayout(titleLines.length, data.companyName);
  doc.setFontSize(11);
  doc.setFont(PDF_FONT_FAMILY, 'normal');
  doc.text(
    `Termin: ${formatDate(data.bookingDateFrom)} – ${formatDate(data.bookingDateTo)}`,
    PDF_MARGIN_LEFT,
    termY
  );
  if (companyY !== null) {
    doc.text(data.companyName!.trim(), PDF_MARGIN_LEFT, companyY);
  }

  // Build expense table body with group header rows flagged
  type RowMeta = { isGroupHeader: boolean };
  const expenseBody: string[][] = [];
  const expenseMeta: RowMeta[] = [];

  const showKurs = data.expenseGroups.some((g) => g.items.some((i) => i.exchangeRate !== null));
  const formatTotal = (value: number, currency: string | null): string => {
    const code = currency?.trim().toUpperCase() || 'PLN';
    return `${value.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${code}`;
  };

  for (const group of data.expenseGroups) {
    expenseBody.push([group.expenseType, '', '', '', ...(showKurs ? [''] : []), '', '']);
    expenseMeta.push({ isGroupHeader: true });
    for (const item of group.items) {
      expenseBody.push([
        '',
        item.issuer ?? '',
        item.documentNumber ?? '',
        item.paymentMethod ?? '',
        ...(showKurs ? [item.exchangeRate != null ? item.exchangeRate.toFixed(4) : '—'] : []),
        formatTotal(item.total, item.currency),
        formatBookingCurrency(item.totalPln),
      ]);
      expenseMeta.push({ isGroupHeader: false });
    }
  }

  // Expense table — only when there are expense groups
  if (expenseBody.length > 0) autoTable(doc, {
    startY: costsStartY,
    margin: { left: PDF_MARGIN_LEFT, right: PDF_MARGIN_RIGHT },
    head: [['KOSZTY', 'OPIS DOKUMENTU', 'NUMER FAKTURY', 'INFO', ...(showKurs ? ['KURS'] : []), 'RAZEM', 'W PLN']],
    body: expenseBody,
    styles: {
      fontSize: 8,
      cellPadding: 2,
      font: PDF_FONT_FAMILY,
      overflow: 'linebreak',
      valign: 'middle',
    },
    headStyles: {
      fontStyle: 'bold',
      font: PDF_FONT_FAMILY,
      fillColor: [33, 150, 243],
      textColor: 255,
    },
    columnStyles: {
      0: { cellWidth: 30 },
      ...(showKurs
        ? { 4: { halign: 'right', cellWidth: 18 }, 5: { halign: 'right' }, 6: { halign: 'right' } }
        : { 4: { halign: 'right' }, 5: { halign: 'right' } }),
    },
    didParseCell: (hookData) => {
      if (hookData.section === 'body') {
        const meta = expenseMeta[hookData.row.index];
        if (meta?.isGroupHeader) {
          hookData.cell.styles.fillColor = [240, 240, 240];
          hookData.cell.styles.fontStyle = 'bold';
        }
      }
    },
  });

  let currentY = (doc.lastAutoTable?.finalY ?? costsStartY) + 6;

  const pageHeight = doc.internal.pageSize.getHeight();
  if (currentY > pageHeight - 20) {
    doc.addPage();
    currentY = 16;
  }

  // Koszty razem
  currentY = addTextLine(
    doc,
    `Koszty razem: ${formatBookingCurrency(data.totalCosts)}`,
    currentY,
    10,
    'bold'
  );

  // Cash breakdown per group and currency
  for (const group of data.expenseGroups) {
    for (const b of group.cashBreakdown) {
      const isForeign = b.currency !== 'PLN';
      const originalFormatted = `${b.totalOriginal.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${b.currency}`;
      const label = isForeign ? `${originalFormatted} (${formatBookingCurrency(b.totalPln)})` : originalFormatted;
      currentY = addTextLine(
        doc,
        `w tym ${group.expenseType} gotówką: ${label}`,
        currentY,
        9,
        'normal'
      );
    }
  }
  currentY += 4;

  // Revenue table
  const revenue = buildRevenueRows(data.revenueItems);
  autoTable(doc, {
    startY: currentY,
    margin: { left: PDF_MARGIN_LEFT, right: PDF_MARGIN_RIGHT },
    head: [revenue.head],
    body: revenue.body,
    styles: {
      fontSize: 8,
      cellPadding: 2,
      font: PDF_FONT_FAMILY,
      overflow: 'linebreak',
      valign: 'middle',
    },
    headStyles: {
      fontStyle: 'bold',
      font: PDF_FONT_FAMILY,
      fillColor: [33, 150, 243],
      textColor: 255,
    },
    columnStyles: buildRevenueColumnStyles(revenue.showFx),
  });

  currentY = (doc.lastAutoTable?.finalY ?? currentY) + 6;

  if (currentY > pageHeight - 24) {
    doc.addPage();
    currentY = 16;
  }

  currentY = addTextLine(
    doc,
    `Przychód razem: ${formatBookingCurrency(data.totalRevenue)}`,
    currentY,
    10,
    'bold'
  );
  currentY += 2;

  const marginColor: [number, number, number] =
    data.margin > 0 ? [27, 94, 32] : [183, 28, 28];
  addTextLine(
    doc,
    `Marża: ${formatBookingCurrency(data.margin)}`,
    currentY,
    10,
    'bold',
    marginColor
  );

  return doc.output('blob');
};

export const downloadBookingEventReportPdf = (blob: Blob, fileName: string): void => {
  if (!(blob instanceof Blob) || blob.size === 0) {
    throw new Error('Wygenerowany PDF jest pusty.');
  }
  const url = window.URL.createObjectURL(blob);
  try {
    const link = window.document.createElement('a');
    link.href = url;
    link.download = fileName;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  } finally {
    window.URL.revokeObjectURL(url);
  }
};
